import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import clsx from "clsx";
import toast from "react-hot-toast";
import { IoClose, IoCheckmark, IoRefresh } from "react-icons/io5";
import Cookies from "js-cookie";

import axiosInstance from "../../api/axios.instance";

import {
  clientApi,
  clientAuth,
  errorMessage,
} from "../../shared/lib/clientSession";
import { hasLimit, money, nextClipNote } from "../../shared/lib/clientPlan";
import {
  deskContact,
  isValidEmail,
  makeRef,
  sendToDesk,
} from "../../shared/lib/libraryStore";

import { track } from "../../shared/lib/analytics";
import styles from "./LibraryModals.module.scss";
import { prospectRef } from "../../shared/lib/prospectRef";

const ModalShell = ({ title, sub, onClose, narrow, children }) => {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div
        className={clsx(styles.modal, narrow && styles.narrow)}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className={styles.head}>
          <div className={styles.titleBox}>
            <div className={styles.title}>{title}</div>
            {sub && <div className={styles.sub}>{sub}</div>}
          </div>
          <button type="button" className={styles.close} onClick={onClose}>
            <IoClose />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
};

const Pills = ({ label, options, value, onChange }) => (
  <div className={styles.pickGroup}>
    <span>{label}</span>
    <div className={styles.pills}>
      {options.map((o) => (
        <button
          type="button"
          key={o}
          className={clsx(styles.pill, value === o && styles.on)}
          onClick={() => onChange(o)}
        >
          {o}
        </button>
      ))}
    </div>
  </div>
);

// Name + work email are required by the /email endpoint
const useContact = () => {
  const [contact, setContact] = useState(deskContact.get);
  const fields = (
    <div className={styles.row2}>
      <label className={styles.field}>
        Your name
        <input
          value={contact.name}
          maxLength={40}
          onChange={(e) => {
          // read now: React 16 pools events, e.target is gone inside the updater
          const next = e.target.value;
          setContact((c) => ({ ...c, name: next }));
        }}
          placeholder="Jane Editor"
        />
      </label>
      <label className={styles.field}>
        Work email
        <input
          type="email"
          value={contact.email}
          onChange={(e) => {
          // read now: React 16 pools events, e.target is gone inside the updater
          const next = e.target.value;
          setContact((c) => ({ ...c, email: next }));
        }}
          placeholder="jane@newsroom.com"
        />
      </label>
    </div>
  );
  const validate = () => {
    if (!contact.name.trim()) {
      toast.error("Enter your name");
      return false;
    }
    if (!isValidEmail(contact.email)) {
      toast.error("Enter a valid work email");
      return false;
    }
    return true;
  };
  return { contact, fields, validate };
};

const SentSteps = ({ steps }) => (
  <div className={styles.steps}>
    {steps.map((s, i) => (
      <div className={styles.step} key={s.label}>
        <div className={styles.stepRail}>
          <span
            className={clsx(
              styles.stepDot,
              i === 0 && styles.done,
              i === 1 && styles.current
            )}
          >
            {i === 0 && <IoCheckmark />}
          </span>
          <span className={styles.stepLine} />
        </div>
        <div className={clsx(styles.stepText, i > 1 && styles.muted)}>
          <strong>{s.label}</strong>
          <span>{s.sub}</span>
        </div>
      </div>
    ))}
  </div>
);

const useSubmit = () => {
  const [sending, setSending] = useState(false);
  const run = async (fn) => {
    setSending(true);
    try {
      await fn();
      return true;
    } catch (err) {
      console.log(err);
      toast.error(
        "Could not send. Please try again or email licensing@vbear.media"
      );
      return false;
    } finally {
      setSending(false);
    }
  };
  return { sending, run };
};

/* ─── Request Footage ─────────────────────────────── */

const RANGES = ["Last 24 hours", "Last 48 hours", "Last 7 days", "Any time"];
const DEADLINES = ["Within 2 hours", "Today", "Within 48 hours", "This week"];
const ORIENTS = ["Horizontal", "Vertical", "Any"];
const USAGES = ["Broadcast", "Digital", "Social", "Commercial"];

export const RequestFootageModal = ({ onClose, initialWhat = "" }) => {
  const { contact, fields, validate } = useContact();
  const { sending, run } = useSubmit();
  const [form, setForm] = useState({
    what: initialWhat,
    loc: "",
    range: RANGES[1],
    deadline: DEADLINES[1],
    orient: ORIENTS[0],
    usage: USAGES[1],
    notes: "",
  });
  const [sent, setSent] = useState(null);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const send = async () => {
    if (!form.what.trim()) {
      toast.error("Describe the footage you need first");
      return;
    }
    if (!validate()) return;
    const ref = makeRef("VB-REQ");
    const ok = await run(() =>
      sendToDesk({
        ...contact,
        subject: `Footage request ${ref} — ${form.deadline}`,
        lines: [
          `Footage request ${ref}`,
          "",
          `What: ${form.what}`,
          `Location: ${form.loc || "—"}`,
          `Date range: ${form.range}`,
          `Deadline: ${form.deadline}`,
          `Orientation: ${form.orient}`,
          `Usage: ${form.usage}`,
          form.notes && `Notes: ${form.notes}`,
        ],
      })
    );
    if (ok) {
      // the sourcing desk tracks the request; the email above is the backup
      axiosInstance
        .post(
          "/sourcing/requests",
          { ref, ...form, name: contact.name, email: contact.email, prospect: prospectRef() },
          { headers: Cookies.get("vb-client-token") ? { "X-Client-Token": Cookies.get("vb-client-token") } : {} }
        )
        .catch(() => {});
      track("footage_request", { usage: form.usage, deadline: form.deadline });
      setSent(ref);
    }
  };

  return (
    <ModalShell
      title={sent ? "Request sent" : "Request Footage"}
      sub="VBear Sourcing Desk · staffed 24/7"
      onClose={onClose}
    >
      {!sent ? (
        <>
          <label className={styles.field}>
            What footage are you looking for?
            <textarea
              rows={3}
              value={form.what}
              onChange={(e) => set("what")(e.target.value)}
              placeholder="Looking for flooding footage from Northern Italy filmed during the last 48 hours. Prefer horizontal smartphone footage."
            />
          </label>
          <div className={styles.row3}>
            <label className={styles.field}>
              Location
              <input
                value={form.loc}
                onChange={(e) => set("loc")(e.target.value)}
                placeholder="City, region or country"
              />
            </label>
            <label className={styles.field}>
              Date range
              <select
                value={form.range}
                onChange={(e) => set("range")(e.target.value)}
              >
                {RANGES.map((o) => (
                  <option key={o}>{o}</option>
                ))}
              </select>
            </label>
            <label className={styles.field}>
              Deadline
              <select
                value={form.deadline}
                onChange={(e) => set("deadline")(e.target.value)}
              >
                {DEADLINES.map((o) => (
                  <option key={o}>{o}</option>
                ))}
              </select>
            </label>
          </div>
          <Pills
            label="Orientation"
            options={ORIENTS}
            value={form.orient}
            onChange={set("orient")}
          />
          <Pills
            label="Usage"
            options={USAGES}
            value={form.usage}
            onChange={set("usage")}
          />
          <label className={styles.field}>
            Additional notes
            <input
              value={form.notes}
              onChange={(e) => set("notes")(e.target.value)}
              placeholder="Budget, exclusivity, contact on the desk"
            />
          </label>
          {fields}
          <button
            type="button"
            className={styles.primary}
            disabled={sending}
            onClick={send}
          >
            {sending ? "Sending…" : "Send Request"}
          </button>
        </>
      ) : (
        <>
          <SentSteps
            steps={[
              { label: "Request received", sub: "Logged with the sourcing desk" },
              {
                label: "Searching contributor network",
                sub: "Researchers are checking new uploads and contacts",
              },
              {
                label: "Clearance and delivery",
                sub: `We reply to ${contact.email} with matching clips`,
              },
            ]}
          />
          <div className={styles.summary}>
            <span className={styles.ref}>{sent}</span>
            <span>
              {form.what.slice(0, 140)} · {form.deadline}
            </span>
          </div>
          <button type="button" className={styles.secondary} onClick={onClose}>
            Done
          </button>
        </>
      )}
    </ModalShell>
  );
};

/* ─── Ask about this clip ─────────────────────────── */

const ASK_TOPICS = [
  "Licence price",
  "Exclusivity",
  "Releases",
  "Raw footage / other angles",
  "Filming details",
];

export const AskClipModal = ({ onClose, clip }) => {
  const { contact, fields, validate } = useContact();
  const { sending, run } = useSubmit();
  const prefix = `About VBEAR-${clip.videoId}: `;
  const [topics, setTopics] = useState([]);
  const [question, setQuestion] = useState(prefix);
  const [usage, setUsage] = useState(USAGES[1]);
  const [sent, setSent] = useState(null);

  const toggleTopic = (t) =>
    setTopics((list) =>
      list.includes(t) ? list.filter((x) => x !== t) : [...list, t]
    );

  const send = async () => {
    const text = question.replace(prefix, "").trim();
    if (!text && !topics.length) {
      toast.error("Write your question or pick a topic");
      return;
    }
    if (!validate()) return;
    const ref = makeRef("VB-ASK");
    const ok = await run(() =>
      sendToDesk({
        ...contact,
        subject: `Question ${ref} — VBEAR-${clip.videoId}`,
        lines: [
          `Question about VBEAR-${clip.videoId} · ${clip.title}`,
          `https://vbear.media/library/${clip.videoId}`,
          "",
          topics.length && `Topics: ${topics.join(", ")}`,
          `Planned usage: ${usage}`,
          "",
          text || "(no message)",
        ],
      })
    );
    if (ok) {
      track("clip_question", { video_id: String(clip.videoId), usage, topics: topics.join(", ") });
      setSent(ref);
    }
  };

  return (
    <ModalShell
      title={sent ? "Request received" : "Ask about this clip"}
      sub="VBear licensing desk · usually replies within 1 hour"
      onClose={onClose}
    >
      {!sent ? (
        <>
          <div className={styles.clipBox}>
            <div
              className={styles.clipThumb}
              style={
                clip.screen ? { backgroundImage: `url(${clip.screen})` } : undefined
              }
            />
            <div className={styles.clipText}>
              <strong>{clip.title}</strong>
              <span>VBEAR-{clip.videoId}</span>
            </div>
          </div>
          <div className={styles.pickGroup}>
            <span>What would you like to know?</span>
            <div className={styles.pills}>
              {ASK_TOPICS.map((t) => (
                <button
                  type="button"
                  key={t}
                  className={clsx(styles.pill, topics.includes(t) && styles.on)}
                  onClick={() => toggleTopic(t)}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>
          <label className={styles.field}>
            Your question
            <textarea
              rows={4}
              autoFocus
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
            />
          </label>
          <Pills
            label="Planned usage"
            options={USAGES}
            value={usage}
            onChange={setUsage}
          />
          {fields}
          <div className={styles.hint}>
            Creator details stay confidential. Licensing answers on behalf of
            the creator.
          </div>
          <button
            type="button"
            className={styles.primary}
            disabled={sending}
            onClick={send}
          >
            {sending ? "Sending…" : "Send Question"}
          </button>
        </>
      ) : (
        <>
          <SentSteps
            steps={[
              { label: "Question received", sub: "Logged with VBear licensing" },
              {
                label: "Licensing is checking the clip",
                sub: "Rights, releases and file details",
              },
              { label: "Answer by email", sub: `We reply to ${contact.email}` },
            ]}
          />
          <div className={styles.summary}>
            <span className={styles.ref}>{sent}</span>
            <span>
              VBEAR-{clip.videoId}
              {topics.length ? ` · ${topics.join(", ")}` : ""}
            </span>
          </div>
          <button type="button" className={styles.secondary} onClick={onClose}>
            Done
          </button>
        </>
      )}
    </ModalShell>
  );
};

/* ─── Request an account ──────────────────────────── */

const CLIPS_PER_MONTH = ["1–5", "6–20", "21–50", "50+", "Not sure yet"];
const ORG_TYPES = ["Broadcaster", "Publisher", "Agency", "Production", "Brand"];
const PLANNED_USE = ["News", "Documentary", "Social", "Advertising"];

export const RequestAccountModal = ({ onClose }) => {
  const { contact, fields, validate } = useContact();
  const { sending, run } = useSubmit();
  const [form, setForm] = useState({
    company: "",
    job: "",
    country: "",
    clips: CLIPS_PER_MONTH[1],
    org: ORG_TYPES[0],
    use: PLANNED_USE[0],
    notes: "",
  });
  const [sent, setSent] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const send = async () => {
    if (!validate()) return;
    if (!form.company.trim()) {
      toast.error("Enter your company");
      return;
    }
    const ok = await run(() =>
      sendToDesk({
        ...contact,
        subject: `Library account request — ${form.company}`,
        lines: [
          "New VBear Library account request",
          "",
          `Company: ${form.company}`,
          `Job title: ${form.job || "—"}`,
          `Country: ${form.country || "—"}`,
          `Clips per month: ${form.clips}`,
          `Organisation type: ${form.org}`,
          `Planned use: ${form.use}`,
          form.notes && `Notes: ${form.notes}`,
        ],
      })
    );
    if (ok) {
      track("account_request", { organization_type: form.org, planned_use: form.use, clips_per_month: form.clips });
      setSent(true);
    }
  };

  if (sent) {
    return (
      <ModalShell title="Request received" onClose={onClose} narrow>
        <div className={styles.sub}>
          The VBear licensing team will reply to {contact.email} within 1
          business day. Until then you can keep searching and watching
          previews.
        </div>
        <button type="button" className={styles.secondary} onClick={onClose}>
          Done
        </button>
      </ModalShell>
    );
  }

  return (
    <ModalShell
      title="Request a VBear account"
      sub="Accounts are for media companies, agencies and production teams. The VBear licensing team reviews each request and replies within 1 business day."
      onClose={onClose}
    >
      {fields}
      <div className={styles.row3}>
        <label className={styles.field}>
          Company
          <input
            value={form.company}
            onChange={(e) => set("company")(e.target.value)}
          />
        </label>
        <label className={styles.field}>
          Job title
          <input value={form.job} onChange={(e) => set("job")(e.target.value)} />
        </label>
        <label className={styles.field}>
          Country
          <input
            value={form.country}
            onChange={(e) => set("country")(e.target.value)}
          />
        </label>
      </div>
      <label className={styles.field}>
        Clips per month
        <select
          value={form.clips}
          onChange={(e) => set("clips")(e.target.value)}
        >
          {CLIPS_PER_MONTH.map((o) => (
            <option key={o}>{o}</option>
          ))}
        </select>
      </label>
      <Pills
        label="Organisation type"
        options={ORG_TYPES}
        value={form.org}
        onChange={set("org")}
      />
      <Pills
        label="Planned use"
        options={PLANNED_USE}
        value={form.use}
        onChange={set("use")}
      />
      <label className={styles.field}>
        Anything else (optional)
        <textarea
          rows={2}
          value={form.notes}
          onChange={(e) => set("notes")(e.target.value)}
        />
      </label>
      <button
        type="button"
        className={styles.primary}
        disabled={sending}
        onClick={send}
      >
        {sending ? "Sending…" : "Request account"}
      </button>
      <div className={styles.altLine}>
        Already have an account?{" "}
        <Link to="/login?as=company" onClick={onClose}>
          Log in
        </Link>
      </div>
    </ModalShell>
  );
};

/* ─── License request (single clip or a collection) ─ */

// the last licence request's usage / project / programme (guests: this browser)
const LAST_LICENSE_KEY = "vb_last_license";
const lastLicenseLocal = {
  get: () => {
    try {
      return JSON.parse(localStorage.getItem(LAST_LICENSE_KEY) || "null");
    } catch (e) {
      return null;
    }
  },
  set: (v) => {
    try {
      localStorage.setItem(LAST_LICENSE_KEY, JSON.stringify(v));
    } catch (e) {
      // private mode: not remembered
    }
  },
};

const LICENSE_USAGES = [
  { label: "Editorial", hint: "News and current affairs" },
  { label: "Broadcast", hint: "Linear TV" },
  { label: "Digital", hint: "Web, apps, OTT" },
  { label: "Social", hint: "Publisher-owned accounts" },
  { label: "Commercial", hint: "Ads and branded content" },
];

/**
 * @param {object} [client] signed-in client session ({ member, company }):
 *   the request goes into the client account instead of an email
 * @param {function} [onDone] called after a client request is created
 */
export const LicenseRequestModal = ({ onClose, clips, title, client, onDone }) => {
  const { contact, fields, validate } = useContact();
  const { sending, run } = useSubmit();
  const [usage, setUsage] = useState([LICENSE_USAGES[0].label]);
  const [form, setForm] = useState({ project: "", publication: "", notes: "" });
  const [sent, setSent] = useState(null);
  const [result, setResult] = useState(null); // client mode: API response
  const [planUsage, setPlanUsage] = useState(null);
  const [last, setLast] = useState(null); // what was entered on the previous request
  const plan = client?.company?.plan;
  useEffect(() => {
    if (!client) {
      setLast(lastLicenseLocal.get());
      return;
    }
    clientApi
      .get("/client/licenses/last")
      .then(({ data }) => setLast(data.apiData))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const lastUsage = (last?.usage || []).filter((u) => LICENSE_USAGES.some((x) => x.label === u));
  const hasLast = !!last && (lastUsage.length > 0 || !!last.project || !!last.publication);
  const sameAsLast =
    hasLast &&
    lastUsage.join() === usage.join() &&
    (last.project || "") === form.project &&
    (last.publication || "") === form.publication;
  const useLast = () => {
    if (lastUsage.length) setUsage(lastUsage);
    setForm((f) => ({ ...f, project: last.project || "", publication: last.publication || "" }));
  };
  const remember = () => lastLicenseLocal.set({ usage, project: form.project, publication: form.publication });
  const clipParams = () => ({
    video_id: clips.map((c) => c.videoId).slice(0, 5).join(","),
    clip_count: clips.length,
  });
  // the modal opens only from a License button: one license_click per click
  useEffect(() => {
    track("license_click", clipParams());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (!client || !hasLimit(plan)) return;
    clientApi
      .get("/client/usage")
      .then(({ data }) => setPlanUsage(data.apiData))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // How many of the requested clips go over the plan limit
  const overCount =
    planUsage && hasLimit(plan) ? Math.max(0, clips.length - planUsage.remaining) : 0;
  const set = (k) => (e) => {
          // read now: React 16 pools events, e.target is gone inside the updater
          const next = e.target.value;
          setForm((f) => ({ ...f, [k]: next }));
        };

  const toggleUsage = (u) =>
    setUsage((list) =>
      list.includes(u) ? list.filter((x) => x !== u) : [...list, u]
    );

  const ids = clips.map((c) => `VBEAR-${c.videoId}`);
  const commercial = usage.includes("Commercial");

  const send = async () => {
    if (!usage.length) {
      toast.error("Pick at least one usage");
      return;
    }
    if (client) {
      const ok = await run(async () => {
        const { data } = await clientApi.post("/client/licenses", {
          videoIds: clips.map((c) => c.videoId),
          usage,
          ...form,
        });
        setResult(data.apiData);
        remember();
        track("license_request", {
          ...clipParams(),
          usage: usage.join(", "),
          method: "client_account",
          approval: data.apiData?.approved ? "instant" : "pending",
        });
      });
      if (ok) {
        if (onDone) onDone();
      }
      return;
    }
    if (!validate()) return;
    const ref = makeRef("VB-LIC");
    const ok = await run(() =>
      sendToDesk({
        ...contact,
        subject: `License request ${ref} — ${ids.slice(0, 3).join(", ")}${
          ids.length > 3 ? ` +${ids.length - 3}` : ""
        }`,
        lines: [
          `License request ${ref}`,
          "",
          ...clips.map(
            (c) =>
              `VBEAR-${c.videoId} · ${c.title} · https://vbear.media/library/${c.videoId}`
          ),
          "",
          `Usage: ${usage.join(", ")}`,
          `Project: ${form.project || "—"}`,
          `Publication / Program: ${form.publication || "—"}`,
          form.notes && `Notes: ${form.notes}`,
        ],
      })
    );
    if (ok) {
      track("license_request", { ...clipParams(), usage: usage.join(", "), method: "request_form" });
      remember();
      setSent(ref);
    }
  };

  if (result) {
    const approved = result.approved;
    return (
      <ModalShell
        title={approved ? "Licensed" : "License request sent"}
        sub={`${client.company.name} · ${client.company.agreement?.name || ""}`}
        onClose={onClose}
      >
        <SentSteps
          steps={
            approved
              ? [
                  { label: "Covered by your agreement", sub: "Approved automatically" },
                  { label: "Master files ready", sub: "Download from the clip page or your account" },
                ]
              : [
                  { label: "Request received", sub: "In your account under License requests" },
                  { label: "VBear licensing review", sub: "Usually within 1 hour" },
                  { label: "Master download", sub: `We email ${client.member.email} when it's approved` },
                ]
          }
        />
        <div className={styles.summary}>
          <span className={styles.ref}>{result.ref}</span>
          <span>
            {result.requested} clip{result.requested === 1 ? "" : "s"}{" "}
            {approved ? "licensed" : "requested"}
            {result.overage
              ? ` · ${result.overage} over the plan limit (${money(client.company.plan.overagePrice, client.company.plan.currency)} each)`
              : ""}
            {result.alreadyCovered
              ? ` · ${result.alreadyCovered} already licensed or requested`
              : ""}
          </span>
        </div>
        <Link to="/library/account" className={styles.secondary} onClick={onClose}>
          Open my account
        </Link>
      </ModalShell>
    );
  }

  return (
    <ModalShell
      title={sent ? "License request sent" : title || "Request License"}
      sub={
        sent
          ? null
          : clips.length === 1
          ? "VBear licensing usually replies within 1 hour"
          : `${clips.length} clips · VBear licensing usually replies within 1 hour`
      }
      onClose={onClose}
    >
      {!sent ? (
        <>
          {clips.length === 1 && (
            <div className={styles.clipBox}>
              <div
                className={styles.clipThumb}
                style={
                  clips[0].screen
                    ? { backgroundImage: `url(${clips[0].screen})` }
                    : undefined
                }
              />
              <div className={styles.clipText}>
                <strong>{clips[0].title}</strong>
                <span>VBEAR-{clips[0].videoId}</span>
              </div>
            </div>
          )}
          {hasLast && !sameAsLast && (
            <button type="button" className={styles.useLast} onClick={useLast}>
              <IoRefresh />
              <span>
                <b>Use the same info as last time</b>
                <small>{[lastUsage.join(", "), last.project, last.publication].filter(Boolean).join(" · ")}</small>
              </span>
            </button>
          )}
          <div className={styles.pickGroup}>
            <span>Usage</span>
            <div className={styles.pills}>
              {LICENSE_USAGES.map((u) => (
                <button
                  type="button"
                  key={u.label}
                  title={u.hint}
                  className={clsx(
                    styles.pill,
                    usage.includes(u.label) && styles.on
                  )}
                  onClick={() => toggleUsage(u.label)}
                >
                  {u.label}
                </button>
              ))}
            </div>
          </div>
          <div className={styles.row2}>
            <label className={styles.field}>
              Project name
              <input value={form.project} onChange={set("project")} />
            </label>
            <label className={styles.field}>
              Publication / Program
              <input value={form.publication} onChange={set("publication")} />
            </label>
          </div>
          <label className={styles.field}>
            Notes (optional)
            <textarea rows={2} value={form.notes} onChange={set("notes")} />
          </label>
          {client ? (
            <>
              {hasLimit(plan) && planUsage && (
                <div className={clsx(styles.hint, overCount > 0 && styles.warn)}>
                  {overCount > 0
                    ? `${overCount} of ${clips.length} clip${clips.length === 1 ? "" : "s"} over your plan limit · billed at ${money(plan.overagePrice, plan.currency)} each on your next invoice`
                    : nextClipNote(plan, planUsage).text}
                </div>
              )}
              <div className={styles.hint}>
                {hasLimit(plan) ? "Licensed immediately · " : ""}
                {client.member.name || client.member.email} · {client.company.name}
              </div>
            </>
          ) : (
            fields
          )}
          {commercial && (
            <div className={styles.hint}>
              Commercial use needs model and property releases. Licensing
              confirms them before the master is delivered.
            </div>
          )}
          <button
            type="button"
            className={styles.primary}
            disabled={sending}
            onClick={send}
          >
            {sending ? "Sending…" : "Request License"}
          </button>
        </>
      ) : (
        <>
          <SentSteps
            steps={[
              { label: "Request received", sub: "Logged with VBear licensing" },
              {
                label: "Rights check",
                sub: "Licensing confirms usage, territory and releases",
              },
              {
                label: "Quote and master delivery",
                sub: `We reply to ${contact.email}`,
              },
            ]}
          />
          <div className={styles.summary}>
            <span className={styles.ref}>{sent}</span>
            <span>
              {ids.slice(0, 4).join(", ")}
              {ids.length > 4 ? ` +${ids.length - 4}` : ""} · {usage.join(", ")}
            </span>
          </div>
          <button type="button" className={styles.secondary} onClick={onClose}>
            Done
          </button>
        </>
      )}
    </ModalShell>
  );
};

/* ─── Client sign-in ──────────────────────────────── */

export const ClientLoginModal = ({ onClose, reason, onRequestAccount }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!isValidEmail(email) || !password) {
      toast.error("Enter your work email and password");
      return;
    }
    setSending(true);
    try {
      const s = await clientAuth.login(email.trim(), password);
      toast.success(`Signed in · ${s.company.name}`);
      onClose();
    } catch (err) {
      toast.error(errorMessage(err, "Could not sign in"));
    } finally {
      setSending(false);
    }
  };

  return (
    <ModalShell
      title="Log in"
      sub={reason || "Media company account · VBear Video Library"}
      onClose={onClose}
      narrow
    >
      <form className={styles.form} onSubmit={submit}>
        <label className={styles.field}>
          Work email
          <input
            type="email"
            autoFocus
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>
        <label className={styles.field}>
          Password
          <input
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </label>
        <button type="submit" className={styles.primary} disabled={sending}>
          {sending ? "Signing in…" : "Log in"}
        </button>
      </form>
      <div className={styles.altLine}>
        <Link to="/login?as=company&forgot=1" onClick={onClose}>
          Forgot password?
        </Link>
      </div>
      <div className={styles.altLine}>
        New to VBear?{" "}
        <button type="button" onClick={onRequestAccount}>
          Request an account
        </button>
      </div>
      <div className={styles.altLine}>
        Creator or VBear team?{" "}
        <Link to="/login?as=creator" onClick={onClose}>
          Log in here
        </Link>
      </div>
    </ModalShell>
  );
};

/* ─── Save search ─────────────────────────────────── */

/**
 * @param {boolean} [canAlert] signed-in client: email alerts available
 * @param {boolean} [semantic] AI query — alerts match its words in titles/tags
 * onSave(name, alert)  alert: null | "instant" | "hourly" | "daily"
 */
export const SaveSearchModal = ({ onClose, defaultName, desc, onSave, canAlert, semantic }) => {
  const [name, setName] = useState(defaultName || "");
  const [alertOn, setAlertOn] = useState(false);
  const [freq, setFreq] = useState("instant");

  const confirm = () => {
    if (!name.trim()) {
      toast.error("Name the search");
      return;
    }
    onSave(name.trim(), canAlert && alertOn ? freq : null);
    onClose();
  };

  return (
    <ModalShell title="Save search" onClose={onClose} narrow>
      <label className={styles.field}>
        Name
        <input
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && confirm()}
        />
      </label>
      {desc && <div className={styles.hint}>{desc}</div>}
      {canAlert ? (
        <>
          <label className={styles.checkRow}>
            <input
              type="checkbox"
              checked={alertOn}
              onChange={(e) => setAlertOn(e.target.checked)}
            />
            Alert me about new matching footage
          </label>
          {alertOn && (
            <>
              <div className={styles.pills}>
                {[
                  ["instant", "Instant"],
                  ["hourly", "Hourly digest"],
                  ["daily", "Daily digest"],
                ].map(([value, label]) => (
                  <button
                    type="button"
                    key={value}
                    className={clsx(styles.pill, freq === value && styles.on)}
                    onClick={() => setFreq(value)}
                  >
                    {label}
                  </button>
                ))}
              </div>
              {semantic && (
                <div className={styles.hint}>
                  Alerts for this AI search match its words in clip titles and
                  tags.
                </div>
              )}
            </>
          )}
        </>
      ) : (
        <div className={styles.hint}>
          Saved in this browser and shown on the Library home page. Media
          company accounts can also get email alerts for new matching footage.
        </div>
      )}
      <div className={styles.actions}>
        <button type="button" className={styles.secondary} onClick={onClose}>
          Cancel
        </button>
        <button type="button" className={styles.dark} onClick={confirm}>
          Save Search
        </button>
      </div>
    </ModalShell>
  );
};

/* ─── Report a bug ────────────────────────────────── */

export const BugReportModal = ({ onClose }) => {
  const { contact, fields, validate } = useContact();
  const { sending, run } = useSubmit();
  const [text, setText] = useState("");
  const page = `${window.location.pathname}${window.location.search}`;

  const send = async () => {
    if (!text.trim()) {
      toast.error("Describe what went wrong");
      return;
    }
    if (!validate()) return;
    const ok = await run(() =>
      sendToDesk({
        ...contact,
        subject: "Library bug report",
        lines: [
          text,
          "",
          `Page: ${window.location.href}`,
          `Browser: ${navigator.userAgent}`,
          `Viewport: ${window.innerWidth}×${window.innerHeight}`,
        ],
      })
    );
    if (ok) {
      toast.success("Thanks — the report has been sent");
      onClose();
    }
  };

  return (
    <ModalShell title="Report a bug" onClose={onClose} narrow>
      <label className={styles.field}>
        What went wrong?
        <textarea
          rows={4}
          autoFocus
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
      </label>
      {fields}
      <div className={styles.hint}>
        Page and browser details are attached automatically: {page}
      </div>
      <button
        type="button"
        className={styles.primary}
        disabled={sending}
        onClick={send}
      >
        {sending ? "Sending…" : "Send report"}
      </button>
    </ModalShell>
  );
};

/* ─── Profile settings (header gear) ─────────────── */

export const ProfileSettingsModal = ({ onClose, member, company }) => {
  const [name, setName] = useState(member?.name || "");
  const [jobTitle, setJobTitle] = useState(member?.jobTitle || "");
  const [savingProfile, setSavingProfile] = useState(false);
  const [pw, setPw] = useState({ current: "", next: "", repeat: "" });
  const [savingPw, setSavingPw] = useState(false);

  const profileChanged =
    name.trim() !== (member?.name || "") || jobTitle.trim() !== (member?.jobTitle || "");

  const saveProfile = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error("Enter your name");
      return;
    }
    setSavingProfile(true);
    try {
      await clientAuth.updateProfile({ name: name.trim(), jobTitle: jobTitle.trim() });
      toast.success("Profile updated");
    } catch (err) {
      toast.error(errorMessage(err, "Could not save the profile"));
    } finally {
      setSavingProfile(false);
    }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    if (!pw.current) {
      toast.error("Enter your current password");
      return;
    }
    if (pw.next.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }
    if (pw.next !== pw.repeat) {
      toast.error("New passwords don't match");
      return;
    }
    setSavingPw(true);
    try {
      await clientAuth.changePassword({ current: pw.current, next: pw.next });
      toast.success("Password changed");
      setPw({ current: "", next: "", repeat: "" });
    } catch (err) {
      toast.error(errorMessage(err, "Could not change the password"));
    } finally {
      setSavingPw(false);
    }
  };

  const pwField = (key) => (e) => {
    const value = e.target.value;
    setPw((v) => ({ ...v, [key]: value }));
  };

  return (
    <ModalShell
      title="Profile settings"
      sub={[member?.email, company?.name].filter(Boolean).join(" · ")}
      onClose={onClose}
      narrow
    >
      <form className={styles.form} onSubmit={saveProfile}>
        <div className={styles.sectionLabel}>Profile</div>
        <label className={styles.field}>
          Name
          <input
            autoComplete="name"
            maxLength={80}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </label>
        <label className={styles.field}>
          Job title
          <input
            autoComplete="organization-title"
            maxLength={80}
            placeholder="e.g. Video Editor"
            value={jobTitle}
            onChange={(e) => setJobTitle(e.target.value)}
          />
        </label>
        <button
          type="submit"
          className={styles.primary}
          disabled={savingProfile || !profileChanged}
        >
          {savingProfile ? "Saving…" : "Save profile"}
        </button>
      </form>

      <form className={clsx(styles.form, styles.formDivided)} onSubmit={savePassword}>
        <div className={styles.sectionLabel}>Change password</div>
        <input type="text" autoComplete="username" value={member?.email || ""} readOnly hidden />
        <label className={styles.field}>
          Current password
          <input
            type="password"
            autoComplete="current-password"
            value={pw.current}
            onChange={pwField("current")}
          />
        </label>
        <div className={styles.row2}>
          <label className={styles.field}>
            New password
            <input
              type="password"
              autoComplete="new-password"
              placeholder="At least 8 characters"
              value={pw.next}
              onChange={pwField("next")}
            />
          </label>
          <label className={styles.field}>
            Repeat new password
            <input
              type="password"
              autoComplete="new-password"
              value={pw.repeat}
              onChange={pwField("repeat")}
            />
          </label>
        </div>
        <button
          type="submit"
          className={clsx(styles.dark, styles.tall)}
          disabled={savingPw || !pw.current || !pw.next}
        >
          {savingPw ? "Changing…" : "Change password"}
        </button>
      </form>
    </ModalShell>
  );
};
